import { useMemo } from 'react'
import { Box, CircularProgress, FormControl, MenuItem, Paper, Select, Typography } from '@mui/material'
import TableChartIcon from '@mui/icons-material/TableChart'
import { DataGrid } from '@mui/x-data-grid'
import { useDispatch, useSelector } from 'react-redux'
import {
  selectBeacon4PerformanceTable,
  selectBeacon4PerformanceTableLoading,
  selectBeacon4PerformanceAvailableKpisByTech,
} from '../reducers/Beacon4PerformanceKpiDataReducer'
import {
  selectBeacon4PerfAggregationMode,
  selectBeacon4PerfTech,
  selectBeacon4PerfMainKpi,
  selectBeacon4PerfTableKpi,
} from '../reducers/Beacon4FiltersReducer'
import { reloadBeacon4PerformanceTableOnly } from '../actions/Beacon4PerformanceModuleActions'

// Must match the Aggregation Mode values the backend accepts. Kept here
// (not imported from FilterBar) so this component owns its own controls.
const AGG_MODES = ['Site', 'Mycom ID', 'Side', 'Polygon', 'Vendor']

/**
 * Renders the aggregation-mode breakdown table:
 *   - Rows    = each group value (Site / Mycom / Polygon / Side / Vendor)
 *   - Columns = dates (respecting the Daily/Weekly/Monthly time aggregation)
 *   - Cells   = KPI value (chosen in the header dropdown) for that group on that date
 *
 * The table has its OWN KPI picker — it defaults to the Main KPI on Apply,
 * then the user can switch to any sub-KPI of the same tech without re-running
 * Apply (only the table is refetched). The header (including the picker) is
 * always rendered so users can change the KPI even while the table is loading
 * or empty.
 */
export default function Beacon4PerformanceAggregationTable() {
  const dispatch  = useDispatch()
  const table     = useSelector(selectBeacon4PerformanceTable)
  const loading   = useSelector(selectBeacon4PerformanceTableLoading)
  const aggMode   = useSelector(selectBeacon4PerfAggregationMode)
  const tech      = useSelector(selectBeacon4PerfTech)
  const mainKpi   = useSelector(selectBeacon4PerfMainKpi)
  const tableKpi  = useSelector(selectBeacon4PerfTableKpi)
  const kpisByTech = useSelector(selectBeacon4PerformanceAvailableKpisByTech)

  // All active KPIs of the current tech, exactly as the backend returns them
  // (Main + every Breakdown/Breakdown2 — independent of which Main KPI drives
  // the dashboard). The Main is annotated in the option list below.
  const kpiOptions = useMemo(() => kpisByTech?.[tech] ?? [], [kpisByTech, tech])

  // What the dropdown shows: fall back to mainKpi until the user has applied
  // (which seeds tableKpi). If neither is set, leave it empty.
  const selectedKpi = tableKpi || mainKpi || ''

  const onChangeKpi = (key) => {
    if (!key || key === selectedKpi) return
    dispatch(reloadBeacon4PerformanceTableOnly({ kpiKey: key }))
  }

  const onChangeAggMode = (mode) => {
    if (!mode || mode === aggMode) return
    dispatch(reloadBeacon4PerformanceTableOnly({ aggMode: mode }))
  }

  const { columns, rows } = useMemo(() => {
    if (!table) return { columns: [], rows: [] }

    const cols = [
      {
        field: 'groupKey',
        headerName: aggMode || 'Group',
        width: 150,
        pinned: 'left',
        sortable: true,
        cellClassName: 'b4p-row-header',
      },
      ...table.dates.map(d => ({
        field: d,
        headerName: d,
        width: 105,
        type: 'number',
        sortable: true,
        align: 'right',
        headerAlign: 'right',
        valueGetter: (params) => params.row[d],
        valueFormatter: (params) => {
          if (params.value == null) return '—'
          return Number(params.value).toFixed(2) + (table.unit === '%' ? '' : '')
        },
      })),
    ]

    const rs = table.rows.map((r, i) => ({
      id: r.groupKey ?? `row-${i}`,
      groupKey: r.groupKey,
      ...r.values,
    }))

    return { columns: cols, rows: rs }
  }, [table, aggMode])

  // The header is ALWAYS rendered (including the KPI dropdown). Only the body
  // below switches between loading / empty / data states.
  return (
    <Paper sx={{
      overflow: 'hidden',
      flex: 1, minHeight: 260, display: 'flex', flexDirection: 'column',
    }}>
      <Box sx={{
        px: 1.5, py: 0.75,
        bgcolor: 'grey.100',
        borderBottom: '1px solid', borderColor: 'divider',
        display: 'flex', alignItems: 'center', gap: 1,
        flexShrink: 0,
      }}>
        <TableChartIcon sx={{ fontSize: 14, color: 'text.secondary' }} />

        {/* KPI picker — defaults to Main KPI after Apply; switching it refetches only the table */}
        <FormControl size="small">
          <Select
            value={kpiOptions.some(k => k.key === selectedKpi) ? selectedKpi : ''}
            onChange={e => onChangeKpi(e.target.value)}
            displayEmpty
            disabled={!kpiOptions.length}
            sx={{
              height: 24, fontSize: 11, minWidth: 220, bgcolor: 'background.paper',
              '& .MuiSelect-select': { py: 0.25 },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
            }}
          >
            <MenuItem value="" disabled sx={{ fontSize: 11, color: 'text.disabled' }}>
              (Select KPI)
            </MenuItem>
            {kpiOptions.map(k => (
              <MenuItem key={k.key} value={k.key} sx={{ fontSize: 11 }}>
                {k.label}{k.key === mainKpi ? '  · main' : ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>by</Typography>

        {/* Aggregation Mode picker — moved here from the FilterBar so all
            table-specific controls live in one place; instant refetch on change. */}
        <FormControl size="small">
          <Select
            value={aggMode || ''}
            onChange={e => onChangeAggMode(e.target.value)}
            sx={{
              height: 24, fontSize: 11, minWidth: 120, bgcolor: 'background.paper',
              '& .MuiSelect-select': { py: 0.25 },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'divider' },
            }}
          >
            {AGG_MODES.map(m => (
              <MenuItem key={m} value={m} sx={{ fontSize: 11 }}>{m}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {table?.unit && (
          <Typography sx={{ fontSize: 10, color: 'text.disabled', ml: 0.5 }}>
            ({table.unit})
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <CircularProgress size={11} />
            <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>Loading…</Typography>
          </Box>
        )}
        {!loading && table && (
          <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>
            {rows.length} rows · {table.dates.length} dates
          </Typography>
        )}
      </Box>

      {/* Body — swaps between empty / data. Loading is shown in the header bar
          so the dropdown stays usable; the body keeps its previous content if any. */}
      <Box sx={{ flex: 1, minHeight: 0, width: '100%', display: 'flex' }}>
        {!table || rows.length === 0 ? (
          <Box sx={{
            flex: 1, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            color: 'text.disabled', p: 4, textAlign: 'center',
          }}>
            <TableChartIcon sx={{ fontSize: 36, opacity: 0.3 }} />
            <Typography sx={{ fontSize: 12, mt: 0.5 }}>
              {loading ? 'Loading table…' : 'No table data yet — pick a KPI and press Apply.'}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <DataGrid
              rows={rows}
              columns={columns}
              density="compact"
              disableRowSelectionOnClick
              pageSizeOptions={[25, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              loading={loading}
              sx={{
                border: 'none',
                fontSize: 11,
                '& .MuiDataGrid-columnHeaders': { bgcolor: '#fafafa', borderBottom: '1px solid', borderColor: 'divider' },
                '& .MuiDataGrid-columnHeaderTitle': { fontSize: 10, fontWeight: 700, color: 'text.secondary' },
                '& .b4p-row-header': { fontWeight: 600, fontFamily: 'monospace', fontSize: 11, bgcolor: 'rgba(0,0,0,0.015)' },
                '& .MuiDataGrid-cell': { borderColor: 'divider', py: 0.5 },
              }}
            />
          </Box>
        )}
      </Box>
    </Paper>
  )
}